import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { apiGet } from '@/utils/api';
import type { User } from '@/types';

/**
 * Hook that wraps the auth store and keeps the user profile in sync
 * via React Query. Fetches /me on mount when authenticated.
 */
export function useAuth() {
  const queryClient = useQueryClient();
  const {
    user,
    accessToken,
    isAuthenticated,
    isLoading: authLoading,
    login,
    logout: storeLogout,
    updateUser,
  } = useAuthStore();

  // Fetch current user profile when authenticated
  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => apiGet<User>('/auth/me'),
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // Sync profile from /me into the store
  useEffect(() => {
    if (profile) {
      updateUser(profile);
    }
  }, [profile, updateUser]);

  const logout = () => {
    storeLogout();
    queryClient.clear();
  };

  return {
    user: profile ?? user,
    isAuthenticated,
    isLoading: authLoading || (!!accessToken && profileLoading),
    error: profileError,
    login,
    logout,
    refetchProfile,
    updateUser,
  };
}

